import type { AffiliateCampaign } from '../../types';
import { isCampaignActive } from '../../mapping';
import type { CampaignFilter } from '../../constants';

/**
 * The Campaigns panel body has four mutually exclusive states. Extracted as a pure
 * decision so the one that regressed stays pinned: a failed fetch must resolve to the
 * ERROR state, never the empty state. A swallowed error used to render as "No campaigns
 * yet", indistinguishable from a brand that genuinely has no campaigns.
 *
 * `empty-filtered` distinguishes "this brand has campaigns, but none match the active/paused
 * filter" from `empty` ("no campaigns at all") — so a filtered-to-zero list never claims the
 * brand has no campaigns.
 */
export type CampaignsPanelState = 'error' | 'empty' | 'empty-filtered' | 'list';

export const campaignsPanelState = ({
  loading,
  hasError,
  total,
  visible,
}: {
  loading: boolean;
  hasError: boolean;
  /** Total campaigns for the brand, before the status filter. */
  total: number;
  /** Campaigns remaining after the status filter. */
  visible: number;
}): CampaignsPanelState => {
  if (hasError) return 'error';
  // Only a settled, error-free empty read is genuinely "no campaigns". While a (re)load is
  // in flight the list keeps its prior rows, so a refetch never flashes the empty state.
  if (!loading && total === 0) return 'empty';
  // Campaigns exist but the active/paused filter hid them all — a distinct state so the owner
  // isn't told "no campaigns yet" when they've merely filtered them out.
  if (!loading && visible === 0) return 'empty-filtered';
  return 'list';
};

/**
 * Apply the campaigns status filter. `all` passes everything through; `active` / `paused` use the
 * single source-of-truth predicate {@link isCampaignActive} so the filter, the list badge and the
 * form's status chip can never disagree about what "active" means.
 */
export const filterCampaigns = (campaigns: AffiliateCampaign[], filter: CampaignFilter): AffiliateCampaign[] => {
  if (filter === 'all') return campaigns;
  const wantActive = filter === 'active';
  return campaigns.filter((campaign) => isCampaignActive(campaign) === wantActive);
};
